import * as d3 from 'd3';

import { CHART_COLORS, renderNotAvailable, applyAxisStyleX, applyAxisStyleY } from '../../../utils/chart-helpers';
import { addMonths } from './battery-life-forecast-utils';
import { clamp } from '@/utils/clamp';

const labelBackgroundColor = 'var(--tds-grey-150)';
const labelTextColor = CHART_COLORS.pointFill;
const axisColor = '#475467';
const primaryStrokeWidth = 2;
const strokeWidthAttribute = 'stroke-width' as const;
const axisStrokeWidth = 1;
const axisTickLength = 4;
const defaultMaxCalendarLifeYears = 15;

interface DataPoint {
  date: Date;
  soh: number;
  kind: 'assembly' | 'today' | '8y' | '12y' | '15y';
}

type ForecastPointKind = Extract<DataPoint['kind'], '8y' | '12y' | '15y'>;

interface ForecastInput {
  years: number;
  soh?: number;
  kind: ForecastPointKind;
}

export interface DrawBatteryLifeForecastChartProperties {
  containerElement: HTMLDivElement;
  svgElement: SVGSVGElement;
  currentSoh?: number;
  currentAgeMonths: number;
  estimatedSoh8Years?: number;
  estimatedSoh12Years?: number;
  estimatedSoh15Years?: number;
  estimatedTotalYears?: number;
}

const defaultChartOuterWidth = 744;
const minChartOuterHeight = 238;
const minChartWidth = 120;
const minChartHeight = 80;

const getMaxCalendarLifeYears = (estimatedTotalYears?: number): number => {
  if (estimatedTotalYears === undefined || !Number.isFinite(estimatedTotalYears) || estimatedTotalYears <= 0) {
    return defaultMaxCalendarLifeYears;
  }

  return estimatedTotalYears;
};

const getPaddedMaxCalendarLifeYears = (maxCalendarLifeYears: number): number => {
  return maxCalendarLifeYears + 1;
};

const createTickYears = (maxCalendarLifeYears: number): number[] => {
  const tickYears = [0, 5, 10, maxCalendarLifeYears].filter((year) => year <= maxCalendarLifeYears);
  return [...new Set(tickYears)];
};

const getDomainEndDate = (today: Date, maxCalendarLifeDate: Date): Date => {
  return today.getTime() > maxCalendarLifeDate.getTime() ? addMonths(today, 12) : maxCalendarLifeDate;
};

export const createSeries = ({
  currentSoh,
  currentAgeMonths,
  estimatedSoh8Years,
  estimatedSoh12Years,
  estimatedSoh15Years,
  estimatedTotalYears = defaultMaxCalendarLifeYears,
}: {
  currentSoh: number;
  currentAgeMonths: number;
  estimatedSoh8Years: number;
  estimatedSoh12Years: number;
  estimatedSoh15Years?: number;
  estimatedTotalYears?: number;
}): {
  series: DataPoint[];
  today: Date;
  assemblyDate: Date;
} => {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const currentAgeMonthsRounded = Math.max(0, Math.round(currentAgeMonths));

  const assemblyDate = addMonths(today, -currentAgeMonthsRounded);

  const maxCalendarLifeYears = getMaxCalendarLifeYears(estimatedTotalYears);
  const forecastInputs: ForecastInput[] = [
    { years: 8, soh: estimatedSoh8Years, kind: '8y' },
    { years: 12, soh: estimatedSoh12Years, kind: '12y' },
    { years: 15, soh: estimatedSoh15Years, kind: '15y' },
  ];
  const forecastPoints = forecastInputs
    .filter((point): point is ForecastInput & { soh: number } => point.soh !== undefined)
    .filter((point) => point.years <= maxCalendarLifeYears)
    .map<DataPoint>((point) => ({
      date: addMonths(assemblyDate, point.years * 12),
      soh: clamp(point.soh, 0, 1),
      kind: point.kind,
    }));

  const series = [
    { date: assemblyDate, soh: 1, kind: 'assembly' },
    { date: today, soh: clamp(currentSoh, 0, 1), kind: 'today' },
    ...forecastPoints,
  ] satisfies DataPoint[];

  const seriesByTime = d3.sort(series, (d) => d.date.getTime());
  return { series: seriesByTime, today, assemblyDate };
};

const drawLabels = (options: {
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  markerPoints: DataPoint[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  chartWidth: number;
}): void => {
  const { g, markerPoints, xScale, yScale, chartWidth } = options;
  const labelGroup = g.append('g').attr('aria-hidden', 'true');
  const labelOffsetX = 8;
  const labelOffsetY = 10;
  const labelPaddingX = 60;

  const labelBoxPaddingX = 6;
  const labelBoxPaddingY = 3;
  const labelBoxCornerRadius = 4;

  const formatPercent = d3.format('.0%');

  const labelData = markerPoints.map((d) => {
    const x = xScale(d.date);
    const y = yScale(d.soh);
    const hasRoomOnRight = x <= chartWidth - labelPaddingX;
    const hasRoomAbove = y >= labelOffsetY + 2;

    return {
      ...d,
      x,
      y,
      dx: hasRoomOnRight ? labelOffsetX : -labelOffsetX,
      dy: hasRoomAbove ? -labelOffsetY : labelOffsetY + 2,
      anchor: hasRoomOnRight ? ('start' as const) : ('end' as const),
      text:
        d.kind === 'today' ? `${formatPercent(d.soh)} (today)` : `${formatPercent(d.soh)} (${d.kind.toUpperCase()})`,
    };
  });

  const labelItems = labelGroup
    .selectAll('g')
    .data(labelData)
    .enter()
    .append('g')
    .attr('transform', (d) => `translate(${d.x + d.dx},${d.y + d.dy})`)
    .style('pointer-events', 'none');

  labelItems
    .append('text')
    .attr('x', 0)
    .attr('y', 0)
    .attr('text-anchor', (d) => d.anchor)
    .attr('dominant-baseline', 'middle')
    .attr('fill', labelTextColor)
    .style('font', 'var(--tds-detail-05)')
    .style('letter-spacing', 'var(--tds-detail-05-ls)')
    .attr('font-weight', 400)
    .text((d) => d.text);

  labelItems.each(function () {
    const item = d3.select(this);
    const textNode = item.select<SVGTextElement>('text').node();
    if (!textNode) return;

    const bbox = textNode.getBBox();

    item
      .insert('rect', 'text')
      .attr('x', bbox.x - labelBoxPaddingX)
      .attr('y', bbox.y - labelBoxPaddingY)
      .attr('width', bbox.width + labelBoxPaddingX * 2)
      .attr('height', bbox.height + labelBoxPaddingY * 2)
      .attr('rx', labelBoxCornerRadius)
      .attr('ry', labelBoxCornerRadius)
      .attr('fill', labelBackgroundColor);
  });
};

function computeSohDomain(series: DataPoint[]): { yMin: number; yMax: number } {
  const minSoh = d3.min(series, (d) => d.soh) ?? 0;
  const maxSoh = d3.max(series, (d) => d.soh) ?? 1;
  const padding = 0.05;

  let yMin = clamp(minSoh - padding, 0, 1);
  let yMax = clamp(maxSoh + padding, 0, 1);

  if (yMax - yMin < 0.1) {
    yMin = clamp(yMin - 0.05, 0, 1);
    yMax = clamp(yMax + 0.05, 0, 1);
  }
  if (yMax <= yMin) {
    yMin = 0;
    yMax = 1;
  }

  return { yMin, yMax };
}

const drawAxes = (options: {
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  chartWidth: number;
  chartHeight: number;
  xTickValues: Date[];
  yTickValues: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
}): void => {
  const { g, chartWidth, chartHeight, xTickValues, yTickValues, xScale, yScale } = options;
  const axesGroup = g.append('g').attr('aria-hidden', 'true');

  axesGroup
    .append('line')
    .attr('x1', 0)
    .attr('y1', chartHeight)
    .attr('x2', chartWidth)
    .attr('y2', chartHeight)
    .attr('stroke', axisColor)
    .attr(strokeWidthAttribute, axisStrokeWidth);

  axesGroup
    .append('line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', chartHeight)
    .attr('stroke', axisColor)
    .attr(strokeWidthAttribute, axisStrokeWidth);

  axesGroup
    .selectAll('.x-axis-tick')
    .data(xTickValues)
    .enter()
    .append('line')
    .attr('class', 'x-axis-tick')
    .attr('x1', (d) => xScale(d))
    .attr('y1', chartHeight)
    .attr('x2', (d) => xScale(d))
    .attr('y2', chartHeight + axisTickLength)
    .attr('stroke', axisColor)
    .attr(strokeWidthAttribute, axisStrokeWidth);

  axesGroup
    .selectAll('.y-axis-tick')
    .data(yTickValues)
    .enter()
    .append('line')
    .attr('class', 'y-axis-tick')
    .attr('x1', -axisTickLength)
    .attr('y1', (d) => yScale(d))
    .attr('x2', 0)
    .attr('y2', (d) => yScale(d))
    .attr('stroke', axisColor)
    .attr(strokeWidthAttribute, axisStrokeWidth);
};

const drawBatteryLifeForecastChart = ({
  containerElement,
  svgElement,
  currentSoh,
  currentAgeMonths,
  estimatedSoh8Years,
  estimatedSoh12Years,
  estimatedSoh15Years,
  estimatedTotalYears,
}: DrawBatteryLifeForecastChartProperties): void => {
  const svg = d3.select(svgElement);
  const { width, height } = containerElement.getBoundingClientRect();

  const innerWidth = width || defaultChartOuterWidth;
  const innerHeight = height || minChartOuterHeight;

  const margin = { top: 32, right: 16, bottom: 20, left: 38 };
  const chartWidth = Math.max(innerWidth - margin.left - margin.right, minChartWidth);
  const chartHeight = Math.max(innerHeight - margin.top - margin.bottom, minChartHeight);

  const maxCalendarLifeYears = getMaxCalendarLifeYears(estimatedTotalYears);
  const paddedMaxCalendarLifeYears = getPaddedMaxCalendarLifeYears(maxCalendarLifeYears);

  svg.selectAll('*').remove();

  const hasData = currentSoh !== undefined && estimatedSoh8Years !== undefined && estimatedSoh12Years !== undefined;

  let assemblyDate: Date;
  let maxCalendarLifeDate: Date;
  let today: Date;
  let series: DataPoint[] | undefined;

  if (hasData) {
    const seriesData = createSeries({
      currentSoh,
      currentAgeMonths,
      estimatedSoh8Years,
      estimatedSoh12Years,
      estimatedSoh15Years,
      estimatedTotalYears: maxCalendarLifeYears,
    });
    series = seriesData.series;
    assemblyDate = seriesData.assemblyDate;
    today = seriesData.today;
    maxCalendarLifeDate = addMonths(assemblyDate, paddedMaxCalendarLifeYears * 12);
  } else {
    const now = new Date();
    today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const currentAgeMonthsRounded = Math.max(0, Math.round(currentAgeMonths));
    assemblyDate = addMonths(today, -currentAgeMonthsRounded);
    maxCalendarLifeDate = addMonths(assemblyDate, paddedMaxCalendarLifeYears * 12);
  }
  const domainEndDate = getDomainEndDate(today, maxCalendarLifeDate);

  const g = svg
    .attr('viewBox', `0 0 ${innerWidth} ${innerHeight}`)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const xScale = d3.scaleTime().domain([assemblyDate, domainEndDate]).range([0, chartWidth]);
  // yScale: [0,1] only if no data, otherwise scale to min/max
  let yScale = d3.scaleLinear().domain([0, 1]).range([chartHeight, 0]);

  if (hasData && series) {
    const { yMin, yMax } = computeSohDomain(series);
    yScale = d3.scaleLinear().domain([yMin, yMax]).range([chartHeight, 0]);
  }

  const tickValues = createTickYears(maxCalendarLifeYears).map((year) => addMonths(assemblyDate, year * 12));
  const xAxis = d3
    .axisBottom<Date>(xScale)
    .tickValues(tickValues)
    .tickFormat((d) => {
      const monthsElapsed = d3.timeMonth.count(assemblyDate, d);
      const yearsElapsed = Math.round(monthsElapsed / 12);
      return `${yearsElapsed}Y`;
    });

  const yAxis = d3.axisLeft<number>(yScale).ticks(3).tickFormat(d3.format('.0%'));

  g.append('g')
    .call(
      d3
        .axisLeft(yScale)
        .ticks(3)
        .tickSize(-chartWidth)
        .tickFormat(() => ''),
    )
    .call((gAxis) => gAxis.select('.domain').remove())
    .selectAll('line')
    .attr('stroke', CHART_COLORS.grid)
    .attr('stroke-opacity', 0.25);

  const yAxisGroup = g.append('g').call(yAxis);
  applyAxisStyleY(yAxisGroup);

  const xAxisGroup = g.append('g').attr('transform', `translate(0,${chartHeight})`).call(xAxis);
  applyAxisStyleX(xAxisGroup);

  drawAxes({
    g,
    chartWidth,
    chartHeight,
    xTickValues: tickValues,
    yTickValues: yScale.ticks(3),
    xScale,
    yScale,
  });

  if (!hasData) {
    renderNotAvailable(g, chartWidth, chartHeight);
    return;
  }

  // series was already computed earlier when hasData is true
  if (!series) {
    throw new Error('Series should be computed when hasData is true');
  }

  const { yMin, yMax } = computeSohDomain(series);
  yScale.domain([yMin, yMax]);

  const line = d3
    .line<DataPoint>()
    .x((d) => xScale(d.date))
    .y((d) => yScale(d.soh))
    .curve(d3.curveMonotoneX);

  const todayIndex = series.findIndex((p) => p.kind === 'today');
  let pastSeries: DataPoint[];
  let futureSeries: DataPoint[];

  if (todayIndex === -1) {
    pastSeries = series;
    futureSeries = [];
  } else {
    pastSeries = series.slice(0, todayIndex + 1);
    futureSeries = series.slice(todayIndex);
  }

  g.append('path')
    .datum(pastSeries)
    .attr('fill', 'none')
    .attr('stroke', CHART_COLORS.line)
    .attr(strokeWidthAttribute, primaryStrokeWidth)
    .attr('d', line);

  if (futureSeries.length >= 2) {
    g.append('path')
      .datum(futureSeries)
      .attr('fill', 'none')
      .attr('stroke', CHART_COLORS.line)
      .attr(strokeWidthAttribute, primaryStrokeWidth)
      .attr('stroke-dasharray', '4 4')
      .attr('d', line);
  }

  const markerPoints = series.filter(
    (p) => p.kind === 'today' || (p.kind !== 'assembly' && p.kind === `${maxCalendarLifeYears}y`),
  );

  const guideLines = g.append('g');
  for (const point of markerPoints) {
    const x = xScale(point.date);
    const y = yScale(point.soh);

    guideLines
      .append('line')
      .attr('x1', 0)
      .attr('y1', y)
      .attr('x2', x)
      .attr('y2', y)
      .attr('stroke', CHART_COLORS.pointStroke)
      .attr(strokeWidthAttribute, 1)
      .attr('stroke-dasharray', '2 2');

    guideLines
      .append('line')
      .attr('x1', x)
      .attr('y1', y)
      .attr('x2', x)
      .attr('y2', chartHeight)
      .attr('stroke', CHART_COLORS.pointStroke)
      .attr(strokeWidthAttribute, 1)
      .attr('stroke-dasharray', '2 2');
  }

  g.selectAll('circle')
    .data(markerPoints)
    .enter()
    .append('circle')
    .attr('cx', (d) => xScale(d.date))
    .attr('cy', (d) => yScale(d.soh))
    .attr('r', 4)
    .attr('fill', CHART_COLORS.pointFill)
    .attr('stroke', CHART_COLORS.pointStroke)
    .attr(strokeWidthAttribute, primaryStrokeWidth);
  // draw labels for marker points
  drawLabels({ g, markerPoints, xScale, yScale, chartWidth });
};

export default drawBatteryLifeForecastChart;
